/**
 * <plushee-sticky-atc>
 * Shows a fixed add to cart bar (mobile) once the product form it targets has
 * scrolled above the viewport, and hides it while any element marked
 * [data-plushee-sticky-stop] (offer recap, final CTA) is on screen.
 */
class PlusheeStickyAtc extends HTMLElement {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Element | null} */
  #trigger = null;

  #triggerPassed = false;

  /** @type {Set<Element>} */
  #visibleStops = new Set();

  connectedCallback() {
    const form = document.getElementById(this.dataset.formId || '');
    this.#trigger = form?.closest('product-form-component') || form;
    if (!this.#trigger) return;

    this.#observer = new IntersectionObserver(this.#handleIntersect);
    this.#observer.observe(this.#trigger);
    document.querySelectorAll('[data-plushee-sticky-stop]').forEach((el) => this.#observer?.observe(el));
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#visibleStops.clear();
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.target === this.#trigger) {
        this.#triggerPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      } else if (entry.isIntersecting) {
        this.#visibleStops.add(entry.target);
      } else {
        this.#visibleStops.delete(entry.target);
      }
    }
    this.#update();
  };

  #update() {
    const visible = this.#triggerPassed && this.#visibleStops.size === 0;
    this.toggleAttribute('data-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
  }
}

if (!customElements.get('plushee-sticky-atc')) {
  customElements.define('plushee-sticky-atc', PlusheeStickyAtc);
}
